import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PremiumBadge } from '../PremiumGate';
import { TIME_SLOTS, DAY_LABELS } from '../../utils/openingHoursUtils';

// Time range descriptions for info tooltips
const TIME_DESCRIPTIONS = {
  morning: '6 AM – 12 PM',
  afternoon: '12 PM – 5 PM',
  evening: '5 PM – 9 PM',
  lateNight: '9 PM – 2 AM',
};

/**
 * OpenAtFilter - Filter component for finding places open at specific times/days
 *
 * Features:
 * - Select multiple days of the week
 * - Select multiple time slots (Morning, Afternoon, Evening, Late Night)
 * - Shows active filter indicator when filter is applied
 * - Premium-gated feature
 */
function OpenAtFilter({
  selectedDays,
  selectedSlots,
  onFilterChange,
  onClearFilter,
  canFilterByOpenAt,
  matchCount,
  isActive,
}) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleButtonClick = () => {
    if (!canFilterByOpenAt) {
      navigate('/settings/premium');
      return;
    }
    setIsOpen(!isOpen);
  };

  const toggleDay = (dayIndex) => {
    const newDays = selectedDays.includes(dayIndex)
      ? selectedDays.filter(d => d !== dayIndex)
      : [...selectedDays, dayIndex];
    onFilterChange(newDays, selectedSlots);
  };

  const toggleSlot = (slotKey) => {
    const newSlots = selectedSlots.includes(slotKey)
      ? selectedSlots.filter(s => s !== slotKey)
      : [...selectedSlots, slotKey];
    onFilterChange(selectedDays, newSlots);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onClearFilter();
  };

  // Get current day for "today" highlight
  const today = new Date().getDay();

  // Check if user has made partial selection (need both days and times)
  const hasDaysOnly = selectedDays.length > 0 && selectedSlots.length === 0;
  const hasSlotsOnly = selectedSlots.length > 0 && selectedDays.length === 0;
  const hasPartialSelection = hasDaysOnly || hasSlotsOnly;

  return (
    <div className="open-at-filter-wrapper" ref={dropdownRef}>
      <button
        className={`open-at-filter-trigger ${isActive ? 'active' : ''} ${!canFilterByOpenAt ? 'premium-locked' : ''}`}
        onClick={handleButtonClick}
        aria-label="Filter by opening hours"
        aria-expanded={isOpen}
      >
        {/* Calendar with magnifying glass overlay icon */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {/* Calendar base */}
          <rect x="2" y="4" width="15" height="15" rx="2" ry="2"/>
          <line x1="6" y1="2" x2="6" y2="6"/>
          <line x1="13" y1="2" x2="13" y2="6"/>
          <line x1="2" y1="9" x2="17" y2="9"/>
          {/* White background for magnifying glass separation */}
          <circle cx="18" cy="18" r="6.5" fill="var(--bg-white, #fff)" stroke="none"/>
          {/* Magnifying glass in lower right */}
          <circle cx="18" cy="18" r="3.5"/>
          <line x1="20.5" y1="20.5" x2="22" y2="22"/>
        </svg>
        {isActive && (
          <span className="filter-active-indicator" aria-label="Filter active" />
        )}
        {!canFilterByOpenAt && <PremiumBadge small />}
      </button>

      {isOpen && canFilterByOpenAt && (
        <div className="open-at-filter-dropdown">
          <div className="open-at-filter-header">
            <h4>Find Places Open At</h4>
            {(selectedDays.length > 0 || selectedSlots.length > 0) && (
              <button className="clear-filter-btn" onClick={handleClear}>
                Clear
              </button>
            )}
          </div>

          <div className={`open-at-filter-section ${hasSlotsOnly ? 'needs-selection' : ''}`}>
            <label className="section-label">
              Days
              {hasSlotsOnly && <span className="selection-hint">Select at least one</span>}
            </label>
            <div className="day-chips">
              {DAY_LABELS.map((label, index) => (
                <button
                  key={index}
                  className={`day-chip ${selectedDays.includes(index) ? 'selected' : ''} ${index === today ? 'today' : ''}`}
                  onClick={() => toggleDay(index)}
                  aria-pressed={selectedDays.includes(index)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className={`open-at-filter-section ${hasDaysOnly ? 'needs-selection' : ''}`}>
            <label className="section-label">
              Time
              {hasDaysOnly && <span className="selection-hint">Select at least one</span>}
            </label>
            <div className="time-slot-chips">
              {Object.entries(TIME_SLOTS).map(([key, slot]) => (
                <button
                  key={key}
                  className={`time-slot-chip ${selectedSlots.includes(key) ? 'selected' : ''}`}
                  onClick={() => toggleSlot(key)}
                  aria-pressed={selectedSlots.includes(key)}
                  title={TIME_DESCRIPTIONS[key]}
                >
                  <span className="slot-icon">{getSlotIcon(key)}</span>
                  <span className="slot-content">
                    <span className="slot-label">{slot.label}</span>
                    <span className="slot-time">{TIME_DESCRIPTIONS[key]}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {hasPartialSelection && (
            <div className="open-at-filter-footer">
              <span className="selection-prompt">
                Select both days and times to filter
              </span>
            </div>
          )}

          {isActive && matchCount !== null && (
            <div className="open-at-filter-footer">
              <span className="match-count">
                {matchCount === 0
                  ? 'No places match'
                  : `${matchCount} place${matchCount === 1 ? '' : 's'} match${matchCount === 1 ? 'es' : ''}`}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Get icon for each time slot
 */
function getSlotIcon(slotKey) {
  switch (slotKey) {
    case 'morning':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="4"/>
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
        </svg>
      );
    case 'afternoon':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="5"/>
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
        </svg>
      );
    case 'evening':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
          <path d="M19 3v4M21 5h-4"/>
        </svg>
      );
    case 'lateNight':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
        </svg>
      );
    default:
      return null;
  }
}

export default OpenAtFilter;
